import { PlannerValidation, ProductType } from '@inter-ikea-storageone/range';
import { Vector, ComponentIds, TransformUtils, Ray, RaycastUtils, HierarchyUtils } from '@inter-ikea-core/core';
import { hasWorktopCenter, hasWorktopLeft, hasWorktopRight } from '../utils/worktop';
import MoveableWorktopComponent from '../components/MoveableWorktopComponent';
const directionLeft = new Vector(-1, 0, 0);
const directionRight = new Vector(1, 0, 0);
/**
 * When adding or dragging a cabinet assembly we remove the worktop collider so cabinet can align.
 * Then this validation is reposible for checking if the cabinet assembly can be placed in the new position
 * depending on how the worktop can move.
 */
export class AlignWorktopValidation extends PlannerValidation {
    constructor(coreApi) {
        super(coreApi, 'AlignWorktopValidation');
    }
    validateCurrentState() {
        return this.validationOK();
    }
    validateSnap(snapActionCandidate) {
        const entity = snapActionCandidate.guestRoot;
        if (!entity.hasComponent(MoveableWorktopComponent.id)) {
            return this.validationOK();
        }
        const alignment = snapActionCandidate.alignmentInitiatorCandidate;
        if (alignment &&
            (alignment.guestConnector.key === 'align-cabinet-left' ||
                alignment.guestConnector.key === 'align-cabinet-right')) {
            return this.validateAlignmentSnap(alignment);
        }
        else {
            return this.validateNonAlignmentSnap(entity);
        }
    }
    /**
     * Validate a snap action candidate that is an alignment to another cabinet.
     */
    validateAlignmentSnap(alignment) {
        const alignmenGuest = HierarchyUtils.parent(alignment.guest);
        const aligmentHost = HierarchyUtils.parent(alignment.host);
        if (alignmenGuest && aligmentHost && alignment.guestConnector.key === 'align-cabinet-left') {
            return this.validateAlignmentLeft(alignmenGuest, aligmentHost);
        }
        else if (alignmenGuest &&
            aligmentHost &&
            alignment.guestConnector.key === 'align-cabinet-right') {
            return this.validateAlignmentRight(alignmenGuest, aligmentHost);
        }
        return this.validationOK();
    }
    /**
     * Validate a snap action candidate that is not an alignment to another cabinet.
     */
    validateNonAlignmentSnap(entity) {
        return this.canWorktopBePlacedCenter(entity)
            ? this.validationOK()
            : this.validationFail('Cant place cabinet since worktop will collide');
    }
    /**
     * Validate if a cabinet assembly can be aligned to the left of another cabinet assembly (regular or corner)
     */
    validateAlignmentLeft(guest, host) {
        if (this.coreApi.entities.isProductType(host, ProductType.KNOXHULT_CORNER_CABINET_ASM)) {
            const rotationRegularCabinet = TransformUtils.worldTransform(guest).rotation.toEulerDegrees();
            const rotationCornerCabinet = TransformUtils.worldTransform(host).rotation.toEulerDegrees();
            if (Math.round(rotationCornerCabinet.Y) === Math.round(rotationRegularCabinet.Y)) {
                return this.canWorktopBePlacedRight(guest)
                    ? this.validationOK()
                    : this.validationFail('Invalid placement');
            }
        }
        else {
            return this.canWorktopBePlacedRight(guest) && this.canWorktopBePlacedLeft(host)
                ? this.validationOK()
                : this.validationFail('Invalid placement');
        }
        return this.validationOK();
    }
    /**
     * Validate if a cabinet assembly can be aligned to the right of another cabinet assembly (regular or corner)
     */
    validateAlignmentRight(guest, host) {
        if (this.coreApi.entities.isProductType(host, ProductType.KNOXHULT_CORNER_CABINET_ASM)) {
            const rotationRegularCabinet = TransformUtils.worldTransform(guest).rotation.toEulerDegrees();
            const rotationCornerCabinet = TransformUtils.worldTransform(host).rotation.toEulerDegrees();
            if (Math.round(rotationCornerCabinet.Y) === Math.round(rotationRegularCabinet.Y)) {
                return this.canWorktopBePlacedLeft(guest)
                    ? this.validationOK()
                    : this.validationFail('Invalid placement');
            }
        }
        else {
            return this.canWorktopBePlacedLeft(guest) && this.canWorktopBePlacedRight(host)
                ? this.validationOK()
                : this.validationFail('Invalid placement');
        }
        return this.validationOK();
    }
    /**
     * Returns true if the worktop on a specifed cabinet assembly can be placed in center.
     */
    canWorktopBePlacedCenter(cabinetAssembly) {
        const distanceLeft = this.getDistance(cabinetAssembly, directionLeft);
        const distanceRight = this.getDistance(cabinetAssembly, directionRight);
        let leftAlignedCabinetWorktopOverhang = 0;
        let rightAlignedCabinetWorktopOverhang = 0;
        if (distanceLeft.entity &&
            this.coreApi.entities.isProductType(distanceLeft.entity, ProductType.KNOXHULT_CABINET)) {
            const cabinetAssembly = HierarchyUtils.parent(distanceLeft.entity);
            if (cabinetAssembly) {
                leftAlignedCabinetWorktopOverhang = this.getWorktopOverhang(cabinetAssembly, directionRight);
            }
        }
        if (distanceRight.entity &&
            this.coreApi.entities.isProductType(distanceRight.entity, ProductType.KNOXHULT_CABINET)) {
            const cabinetAssembly = HierarchyUtils.parent(distanceRight.entity);
            if (cabinetAssembly) {
                rightAlignedCabinetWorktopOverhang = this.getWorktopOverhang(cabinetAssembly, directionLeft);
            }
        }
        return (distanceLeft.distance >= 10 + leftAlignedCabinetWorktopOverhang &&
            distanceRight.distance >= 10 + rightAlignedCabinetWorktopOverhang);
    }
    /**
     * Returns true if the worktop on a specifed cabinet assembly can be moved to the left.
     */
    canWorktopBePlacedLeft(cabinetAssembly) {
        const distanceLeft = this.getDistance(cabinetAssembly, directionLeft);
        let alignedCabinetWorktopOverhang = 0;
        if (distanceLeft.entity &&
            this.coreApi.entities.isProductType(distanceLeft.entity, ProductType.KNOXHULT_CABINET)) {
            const cabinetAssembly = HierarchyUtils.parent(distanceLeft.entity);
            if (cabinetAssembly) {
                alignedCabinetWorktopOverhang = this.getWorktopOverhang(cabinetAssembly, directionRight);
            }
        }
        return distanceLeft.distance >= 20 + alignedCabinetWorktopOverhang;
    }
    /**
     * Returns true if the worktop on a specifed cabinet assembly can be moved to the right.
     */
    canWorktopBePlacedRight(cabinetAssembly) {
        const distanceRight = this.getDistance(cabinetAssembly, directionRight);
        let alignedCabinetWorktopOverhang = 0;
        if (distanceRight.entity &&
            this.coreApi.entities.isProductType(distanceRight.entity, ProductType.KNOXHULT_CABINET)) {
            const cabinetAssembly = HierarchyUtils.parent(distanceRight.entity);
            if (cabinetAssembly) {
                alignedCabinetWorktopOverhang = this.getWorktopOverhang(cabinetAssembly, directionLeft);
            }
        }
        return distanceRight.distance >= 20 + alignedCabinetWorktopOverhang;
    }
    /**
     * Uses raycast to check distance to nearest entity
     * in the given direction.
     */
    getDistance(cabinetAssembly, direction) {
        const sceneEntity = this.coreApi.entities.getScene();
        const cabinetAssemblySize = cabinetAssembly.getComponent(ComponentIds.TEMPLATE_DATA_COMPONENT_ID)?.size;
        if (!sceneEntity || !cabinetAssemblySize) {
            return { distance: Number.MAX_VALUE, entity: null };
        }
        const transform = TransformUtils.worldTransform(cabinetAssembly);
        const ray = new Ray(transform.position, transform.transformVector(direction));
        const rayHit = RaycastUtils.raycast(ray, sceneEntity, cabinetAssembly);
        return rayHit
            ? { distance: rayHit.distance - cabinetAssemblySize.X / 2, entity: rayHit.entity }
            : { distance: Number.MAX_VALUE, entity: null };
    }
    /**
     * Returns how much the worktop overhangs the cabinet in a specific direction.
     * The overhang is 2cm on one side if the worktop is placed to the left or right,
     * and 1cm in each direction if the worktop is placed in the center.
     */
    getWorktopOverhang(cabinetAssembly, direction) {
        if (hasWorktopCenter(cabinetAssembly, this.coreApi)) {
            return 10;
        }
        if (direction.equals(directionLeft)) {
            return hasWorktopLeft(cabinetAssembly, this.coreApi) ? 20 : 0;
        }
        else {
            return hasWorktopRight(cabinetAssembly, this.coreApi) ? 20 : 0;
        }
    }
}
//# sourceMappingURL=AlignWorktopValidation.js.map